import { useState, useRef, useEffect, useCallback } from "react";
import {
  BrainCircuit,
  Send,
  Trash2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Database,
  Calculator,
  Table2,
  AlertCircle,
  BarChart3,
  Shield,
  BookOpen,
  Copy,
  Check,
  Factory,
  TrendingUp,
  MessageSquare,
  Plus,
  ThumbsUp,
  ThumbsDown,
  X,
  Brain,
  Sparkles,
  Pin,
  Share2,
  Link,
  Star,
  UserCircle,
  Paperclip,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { useChat, useChatModels, type ChatMessage, type ToolCallInfo, type Attachment, type StreamingState } from "@/hooks/use-chat";
import {
  useConversations,
  useDeleteConversation,
  useSubmitFeedback,
  useStarConversation,
} from "@/hooks/use-conversations";
import { useDiscoverPrompts, type DiscoverPrompt } from "@/hooks/use-discover-prompts";
import { useMemories, useCreateMemory, useDeleteMemory } from "@/hooks/use-memories";
import { useSaveArtifact } from "@/hooks/use-artifacts";
import { useCreateShareLink } from "@/hooks/use-sharing";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";

// --- Chart colours (vibrant, readable on both dark and light backgrounds) ---

const CHART_COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#8b5cf6", // violet-500
  "#ef4444", // red-500
  "#06b6d4", // cyan-500
  "#f97316", // orange-500
  "#ec4899", // pink-500
];

// --- Chart spec type ---

interface ChartSpec {
  chart_type: "bar" | "line" | "pie" | "scatter" | "control";
  title: string;
  data: Record<string, unknown>[];
  x_key: string;
  y_keys: string[];
  x_label?: string;
  y_label?: string;
  reference_lines?: Array<{ value: number; label: string; color?: string; style?: "solid" | "dashed" }>;
}

// --- Inline chart renderer ---

function ChartBlock({ spec, onPin, sqlQuery }: { spec: ChartSpec; onPin?: (spec: ChartSpec, sql?: string) => void; sqlQuery?: string }) {
  const { chart_type, title, data, x_key, y_keys, x_label, y_label } = spec;
  const [pinned, setPinned] = useState(false);

  if (!data?.length || !x_key || !y_keys?.length) {
    return <p className="text-xs text-muted-foreground italic">No chart data</p>;
  }

  const commonProps = {
    data,
    margin: { top: 5, right: 20, bottom: 5, left: 10 },
  };

  return (
    <div className="my-3 rounded-lg border border-border bg-card p-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-medium text-muted-foreground">{title}</p>
        {onPin && (
          <button
            onClick={() => { onPin(spec, sqlQuery); setPinned(true); }}
            disabled={pinned}
            className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-xs transition-colors ${
              pinned
                ? "text-primary"
                : "text-muted-foreground hover:text-primary hover:bg-muted/50"
            }`}
            title="Save as artifact"
          >
            <Pin className="h-3 w-3" />
            <span>{pinned ? "Saved" : "Pin"}</span>
          </button>
        )}
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {chart_type === "bar" ? (
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey={x_key} tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={x_label ? { value: x_label, position: "insideBottom", offset: -5, fontSize: 11, fill: "var(--muted-foreground))" } : undefined} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={y_label ? { value: y_label, angle: -90, position: "insideLeft", fontSize: 11, fill: "var(--muted-foreground))" } : undefined} />
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
              {y_keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, color: "var(--muted-foreground))" }} />}
              {y_keys.map((key, i) => (
                <Bar key={key} dataKey={key} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[4, 4, 0, 0]} />
              ))}
            </BarChart>
          ) : chart_type === "line" || chart_type === "control" ? (
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey={x_key} tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={x_label ? { value: x_label, position: "insideBottom", offset: -5, fontSize: 11, fill: "var(--muted-foreground))" } : undefined} />
              <YAxis tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={y_label ? { value: y_label, angle: -90, position: "insideLeft", fontSize: 11, fill: "var(--muted-foreground))" } : undefined} />
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
              {y_keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, color: "var(--muted-foreground))" }} />}
              {y_keys.map((key, i) => (
                <Line key={key} type="monotone" dataKey={key} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
              ))}
              {spec.reference_lines?.map((rl, i) => (
                <ReferenceLine key={`ref-${i}`} y={rl.value}
                  label={{ value: rl.label, position: "right", fontSize: 10, fill: "var(--muted-foreground))" }}
                  stroke={rl.color || (rl.label.toLowerCase().includes("mean") ? "#3b82f6" : "#ef4444")}
                  strokeDasharray={rl.style === "solid" ? undefined : "5 5"}
                  strokeWidth={1.5} />
              ))}
            </LineChart>
          ) : chart_type === "pie" ? (
            <PieChart>
              <Pie data={data} dataKey={y_keys[0]} nameKey={x_key} cx="50%" cy="50%" outerRadius={90} label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                {data.map((_, i) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
            </PieChart>
          ) : (
            <ScatterChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border))" />
              <XAxis dataKey={x_key} type="number" tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={x_label ? { value: x_label, position: "insideBottom", offset: -5, fontSize: 11, fill: "var(--muted-foreground))" } : undefined} />
              <YAxis dataKey={y_keys[0]} type="number" tick={{ fontSize: 11, fill: "var(--muted-foreground))" }} label={y_label ? { value: y_label, angle: -90, position: "insideLeft", fontSize: 11, fill: "var(--muted-foreground))" } : undefined} />
              <Tooltip contentStyle={{ backgroundColor: "var(--popover))", border: "1px solid var(--border))", borderRadius: 8, fontSize: 12, color: "var(--popover-foreground))" }} />
              <Scatter data={data} fill={CHART_COLORS[0]} />
            </ScatterChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// --- Fallback prompts (shown if API returns empty) ---

const FALLBACK_PROMPTS: DiscoverPrompt[] = [
  { id: -1, prompt_text: "What are the top 5 products by total production weight this year?", category: "production" },
  { id: -2, prompt_text: "Show the average part weight and standard deviation for each product", category: "quality" },
  { id: -3, prompt_text: "Which materials have the highest variance from target percentages?", category: "materials" },
  { id: -4, prompt_text: "What are the reject rates by type across all products?", category: "production" },
  { id: -5, prompt_text: "Compare weight trends across injection moulded parts", category: "quality" },
  { id: -6, prompt_text: "What's the production trend by month for the last 12 months?", category: "production" },
];

const CATEGORY_ICONS: Record<string, string> = {
  production: "\u{1F3ED}",
  quality: "\u{1F9EA}",
  materials: "\u{1F9EA}",
  lab: "\u{1F52C}",
  "r&d": "\u{1F4CA}",
  general: "\u{1F4C8}",
};

// --- Tool call display ---

function toolIcon(tool: string) {
  switch (tool) {
    case "run_sql":
      return <Database className="h-3.5 w-3.5" />;
    case "list_tables":
    case "get_table_schema":
      return <Table2 className="h-3.5 w-3.5" />;
    case "calculate":
      return <Calculator className="h-3.5 w-3.5" />;
    case "create_chart":
      return <BarChart3 className="h-3.5 w-3.5" />;
    case "get_spec_limits":
      return <Shield className="h-3.5 w-3.5" />;
    case "search_kb":
      return <BookOpen className="h-3.5 w-3.5" />;
    case "get_product_info":
      return <BookOpen className="h-3.5 w-3.5" />;
    case "get_production_sequence":
      return <Factory className="h-3.5 w-3.5" />;
    case "calculate_process_stats":
      return <TrendingUp className="h-3.5 w-3.5" />;
    default:
      return <Database className="h-3.5 w-3.5" />;
  }
}

function isChartOutput(output: unknown): output is { chart: ChartSpec } {
  return !!output && typeof output === "object" && "chart" in output;
}

function ToolCallBlock({ call, onPinChart, onPinTable, lastSqlQuery }: { call: ToolCallInfo; onPinChart?: (spec: ChartSpec, sql?: string) => void; onPinTable?: (title: string, columns: string[], data: Record<string, unknown>[], sql: string) => void; lastSqlQuery?: string }) {
  const [open, setOpen] = useState(false);
  const [tablePinned, setTablePinned] = useState(false);

  const output = call.output as Record<string, unknown> | undefined;
  const hasError = output && "error" in output;
  const rowCount = output && "row_count" in output ? (output.row_count as number) : null;
  const execMs = output && "execution_ms" in output ? (output.execution_ms as number) : null;
  const hasChart = isChartOutput(call.output);
  const hasTableData = call.tool === "run_sql" && !hasError && output && Array.isArray(output.rows) && (output.rows as unknown[]).length > 0;

  let summary = call.tool;
  if (call.tool === "run_sql") {
    const query = (call.input.query as string) || "";
    summary = query.length > 80 ? query.slice(0, 80) + "..." : query;
  } else if (call.tool === "get_table_schema") {
    summary = `Schema: ${call.input.table_name}`;
  } else if (call.tool === "calculate") {
    summary = `Calculate: ${call.input.expression}`;
  } else if (call.tool === "list_tables") {
    summary = "List tables";
  } else if (call.tool === "create_chart") {
    summary = `Chart: ${(call.input.title as string) || "Visualisation"}`;
  } else if (call.tool === "get_spec_limits") {
    summary = `Spec limits: ${call.input.product_slug}`;
  } else if (call.tool === "search_kb") {
    const kbOutput = call.output as { articles?: Array<{ title: string }> } | undefined;
    const titles = kbOutput?.articles?.map((a) => a.title).join("', '");
    summary = titles ? `KB: '${titles}'` : `KB search: ${call.input.query}`;
  } else if (call.tool === "get_product_info") {
    const prodOutput = call.output as { products?: Array<{ name: string }> } | undefined;
    const names = prodOutput?.products?.map((p) => p.name).join(", ");
    summary = names ? `Products: ${names}` : `Product search: ${call.input.query}`;
  } else if (call.tool === "get_production_sequence") {
    const seqOutput = call.output as { target?: { product_lookup?: string }; before?: unknown[]; after?: unknown[] } | undefined;
    const product = seqOutput?.target?.product_lookup || `batch ${call.input.batch_id}`;
    const total = (seqOutput?.before?.length || 0) + (seqOutput?.after?.length || 0);
    summary = `Sequence: ${product} (${total} context batches)`;
  } else if (call.tool === "calculate_process_stats") {
    const statsOutput = call.output as { cpk?: number; capability_rating?: string; n?: number } | undefined;
    if (statsOutput?.cpk != null) {
      summary = `Stats: Cpk ${statsOutput.cpk} — ${statsOutput.capability_rating} (n=${statsOutput.n})`;
    } else {
      summary = `Process stats (n=${(call.input.values as number[])?.length || "?"})`;
    }
  }

  // Charts render directly — no collapsible wrapper needed
  if (hasChart) {
    return <ChartBlock spec={(call.output as { chart: ChartSpec }).chart} onPin={onPinChart} sqlQuery={lastSqlQuery} />;
  }

  return (
    <div className="my-1.5 rounded-md border border-border/50 bg-muted/30 text-xs">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 transition-colors"
      >
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
        )}
        {toolIcon(call.tool)}
        <span className="truncate font-mono text-muted-foreground">{summary}</span>
        <span className="ml-auto flex items-center gap-2 shrink-0">
          {hasError && <AlertCircle className="h-3 w-3 text-destructive" />}
          {rowCount !== null && (
            <span className="text-muted-foreground">{rowCount} rows</span>
          )}
          {execMs !== null && (
            <span className="text-muted-foreground">{execMs}ms</span>
          )}
          {hasTableData && onPinTable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const rows = output!.rows as Record<string, unknown>[];
                const columns = Object.keys(rows[0]);
                const query = String(call.input.query || "");
                const tableMatch = query.match(/FROM\s+["']?(\w+)["']?/i);
                const title = tableMatch ? tableMatch[1].replace(/^(production_monitor_|qa_production_|research_|raw_materials_)/, "") : "Query result";
                onPinTable(title, columns, rows.slice(0, 200), query);
                setTablePinned(true);
              }}
              disabled={tablePinned}
              className={`flex items-center gap-1 ${tablePinned ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
              title="Save table as artifact"
            >
              <Pin className="h-3 w-3" />
            </button>
          )}
        </span>
      </button>

      {open && (
        <div className="border-t border-border/50 p-3">
          {call.tool === "run_sql" && !!call.input.query && (
            <pre className="mb-2 whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs text-foreground">
              {String(call.input.query)}
            </pre>
          )}

          {hasError ? (
            <p className="text-destructive">{String(output!.error)}</p>
          ) : (
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted p-2 font-mono text-xs text-muted-foreground">
              {JSON.stringify(call.output, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

// --- Markdown components for react-markdown ---

const markdownComponents = {
  table: ({ children, ...props }: React.ComponentProps<"table">) => (
    <div className="my-2 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs" {...props}>{children}</table>
    </div>
  ),
  thead: ({ children, ...props }: React.ComponentProps<"thead">) => (
    <thead className="bg-muted/50" {...props}>{children}</thead>
  ),
  th: ({ children, ...props }: React.ComponentProps<"th">) => (
    <th className="px-3 py-1.5 text-left font-medium text-muted-foreground" {...props}>{children}</th>
  ),
  td: ({ children, ...props }: React.ComponentProps<"td">) => (
    <td className="border-t border-border/50 px-3 py-1.5" {...props}>{children}</td>
  ),
  code: ({ children, className, ...props }: React.ComponentProps<"code">) => {
    const isBlock = className?.includes("language-");
    if (isBlock) {
      return (
        <code className="block rounded-md bg-muted p-3 font-mono text-xs overflow-x-auto" {...props}>
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: React.ComponentProps<"pre">) => (
    <pre className="my-2" {...props}>{children}</pre>
  ),
  h1: ({ children, ...props }: React.ComponentProps<"h1">) => (
    <h3 className="mt-3 mb-1 text-sm font-semibold" {...props}>{children}</h3>
  ),
  h2: ({ children, ...props }: React.ComponentProps<"h2">) => (
    <h4 className="mt-3 mb-1 text-sm font-semibold" {...props}>{children}</h4>
  ),
  h3: ({ children, ...props }: React.ComponentProps<"h3">) => (
    <h5 className="mt-2 mb-1 text-sm font-medium" {...props}>{children}</h5>
  ),
  ul: ({ children, ...props }: React.ComponentProps<"ul">) => (
    <ul className="my-1 ml-6 list-disc space-y-0.5" {...props}>{children}</ul>
  ),
  ol: ({ children, ...props }: React.ComponentProps<"ol">) => (
    <ol className="my-1 ml-6 list-decimal space-y-0.5" {...props}>{children}</ol>
  ),
  li: ({ children, ...props }: React.ComponentProps<"li">) => (
    <li className="text-sm" {...props}>{children}</li>
  ),
  p: ({ children, ...props }: React.ComponentProps<"p">) => (
    <p className="my-1.5 leading-relaxed" {...props}>{children}</p>
  ),
  blockquote: ({ children, ...props }: React.ComponentProps<"blockquote">) => (
    <blockquote className="my-2 border-l-2 border-primary/40 pl-3 text-muted-foreground italic" {...props}>
      {children}
    </blockquote>
  ),
  strong: ({ children, ...props }: React.ComponentProps<"strong">) => (
    <strong className="font-semibold" {...props}>{children}</strong>
  ),
  a: ({ children, href, ...props }: React.ComponentProps<"a">) => (
    <a className="text-primary underline underline-offset-2" href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ),
  img: ({ src, alt, ...props }: React.ComponentProps<"img">) => (
    <img
      src={src}
      alt={alt || ""}
      className="my-2 max-w-xs rounded-lg border border-border"
      loading="lazy"
      {...props}
    />
  ),
  hr: (props: React.ComponentProps<"hr">) => (
    <hr className="my-3 border-border/50" {...props} />
  ),
};

// --- Grouped tool calls (collapsed by default) ---

function ToolCallGroup({ calls, onPinChart, onPinTable }: { calls: ToolCallInfo[]; onPinChart?: (spec: ChartSpec, sql?: string) => void; onPinTable?: (title: string, columns: string[], data: Record<string, unknown>[], sql: string) => void }) {
  const [open, setOpen] = useState(false);

  // Separate chart calls (render inline) from other calls (group them)
  const chartCalls = calls.filter((c) => isChartOutput(c.output));
  const otherCalls = calls.filter((c) => !isChartOutput(c.output));

  // Compute summary stats for the group
  const totalMs = otherCalls.reduce((sum, c) => {
    const out = c.output as Record<string, unknown> | undefined;
    return sum + (out && typeof out.execution_ms === "number" ? out.execution_ms : 0);
  }, 0);

  const errorCount = otherCalls.filter((c) => {
    const out = c.output as Record<string, unknown> | undefined;
    return out && "error" in out;
  }).length;

  const durationLabel = totalMs > 0
    ? totalMs > 1000 ? `${(totalMs / 1000).toFixed(1)}s` : `${totalMs}ms`
    : null;

  return (
    <>
      {/* Grouped non-chart calls */}
      {otherCalls.length > 0 && (
        <div className="my-1.5 rounded-md border border-border/50 bg-muted/30 text-xs">
          <button
            onClick={() => setOpen(!open)}
            className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/50 transition-colors"
          >
            {open ? (
              <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
            )}
            <Database className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">
              {otherCalls.length} tool call{otherCalls.length !== 1 ? "s" : ""}
              {durationLabel && ` (${durationLabel})`}
            </span>
            {errorCount > 0 && (
              <span className="ml-auto flex items-center gap-1 text-destructive">
                <AlertCircle className="h-3 w-3" />
                {errorCount}
              </span>
            )}
          </button>

          {open && (
            <div className="border-t border-border/50 p-1">
              {otherCalls.map((call, i) => (
                <ToolCallBlock key={i} call={call} onPinTable={onPinTable} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chart calls render inline (never collapsed) */}
      {chartCalls.map((call, i) => {
        // Find the last run_sql query before this chart for the "refresh" feature
        const lastSql = [...otherCalls].reverse().find((c) => c.tool === "run_sql")?.input?.query as string | undefined;
        return (
          <ChartBlock key={`chart-${i}`} spec={(call.output as { chart: ChartSpec }).chart} onPin={onPinChart} sqlQuery={lastSql} />
        );
      })}
    </>
  );
}

// --- Message rendering ---

function MessageBubble({ msg, onSendFollowUp, onPinChart, onPinTable }: { msg: ChatMessage; onSendFollowUp?: (q: string) => void; onPinChart?: (spec: ChartSpec, sql?: string) => void; onPinTable?: (title: string, columns: string[], data: Record<string, unknown>[], sql: string) => void }) {
  const isUser = msg.role === "user";

  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`rounded-lg px-4 py-3 ${
          isUser
            ? "max-w-[80%] bg-primary text-primary-foreground"
            : "max-w-[90%] bg-card text-card-foreground border border-border"
        }`}
      >
        {/* Tool calls — grouped and collapsed by default */}
        {!isUser && msg.tool_calls && msg.tool_calls.length > 0 && (
          <div className="mb-2">
            <ToolCallGroup calls={msg.tool_calls} onPinChart={onPinChart} onPinTable={onPinTable} />
          </div>
        )}

        {/* Attachment indicators on user messages */}
        {isUser && msg.attachments && msg.attachments.length > 0 && (
          <div className="mb-1.5 flex flex-wrap gap-1.5">
            {msg.attachments.map((att, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded bg-primary-foreground/10 px-2 py-0.5 text-xs">
                {att.type === "image" ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                <span className="max-w-[120px] truncate">{att.filename}</span>
              </span>
            ))}
          </div>
        )}

        {/* Content */}
        {isUser ? (
          <div className="text-sm leading-relaxed whitespace-pre-wrap">
            {msg.content}
          </div>
        ) : (
          <div className="text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {msg.content}
            </ReactMarkdown>
          </div>
        )}

        {/* Action bar — assistant messages only */}
        {!isUser && msg.content && (
          <ActionBar msg={msg} />
        )}

        {/* Follow-up suggestions — last assistant message only */}
        {!isUser && msg.follow_up_suggestions && msg.follow_up_suggestions.length > 0 && onSendFollowUp && (
          <div className="mt-3 flex flex-wrap gap-2 border-t border-border/50 pt-3">
            {msg.follow_up_suggestions.map((q, i) => (
              <button
                key={i}
                onClick={() => onSendFollowUp(q)}
                className="rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors text-left"
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Analysing indicator with elapsed timer ---

const ANALYSIS_STAGES = [
  { after: 0, text: "Analysing..." },
  { after: 3, text: "Querying database..." },
  { after: 8, text: "Crunching numbers..." },
  { after: 15, text: "Digging deeper..." },
  { after: 25, text: "Still working on it..." },
  { after: 40, text: "Complex query, hang tight..." },
];

const FILE_ANALYSIS_STAGES = [
  { after: 0, text: "Reading files..." },
  { after: 2, text: "Processing attachments..." },
  { after: 5, text: "Analysing content..." },
  { after: 10, text: "Cross-referencing with database..." },
  { after: 20, text: "Digging deeper..." },
  { after: 35, text: "Complex analysis, hang tight..." },
];

function AnalysingIndicator({ hasAttachments }: { hasAttachments?: boolean }) {
  const [elapsed, setElapsed] = useState(0);
  const stages = hasAttachments ? FILE_ANALYSIS_STAGES : ANALYSIS_STAGES;

  useEffect(() => {
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const stage = [...stages].reverse().find((s) => elapsed >= s.after) || stages[0];

  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{stage.text}</span>
        <span className="ml-1 tabular-nums text-xs text-muted-foreground/60">
          {elapsed}s
        </span>
      </div>
    </div>
  );
}

// --- Streaming indicator (real tool calls, text, charts as they arrive) ---

/** Streaming message bubble — only renders once text or charts start arriving. */
function StreamingBubble({ streaming }: { streaming: StreamingState }) {
  const hasContent = streaming.content.length > 0;
  const hasCharts = streaming.liveCharts.length > 0;

  // Nothing to show yet — the StreamingStatus line above the input handles this state
  if (!hasContent && !hasCharts) return null;

  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] rounded-lg border border-border bg-card px-4 py-3 text-sm text-card-foreground">
        {streaming.liveCharts.map((chart, i) => (
          <ChartBlock key={`live-chart-${i}`} spec={chart.spec as ChartSpec} />
        ))}

        {hasContent && (
          <div className="text-sm">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {streaming.content}
            </ReactMarkdown>
            <span className="inline-block w-2 h-4 bg-primary/60 animate-pulse ml-0.5 align-text-bottom" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Status line above the input — shows current tool activity during streaming. */
function StreamingStatus({ streaming }: { streaming: StreamingState }) {
  // Find the latest running tool, or the most recently completed one
  const runningTool = [...streaming.liveTools].reverse().find((t) => t.status === "running");
  const latestDone = [...streaming.liveTools].reverse().find((t) => t.status === "done");
  const activeTool = runningTool || latestDone;
  const doneCount = streaming.liveTools.filter((t) => t.status === "done").length;
  const totalCount = streaming.liveTools.length;

  let statusText = "Thinking...";
  if (activeTool) {
    const toolLabel = activeTool.tool === "run_sql" ? "Querying"
      : activeTool.tool === "search_kb" ? "Searching knowledge base"
      : activeTool.tool === "get_table_schema" ? "Inspecting schema"
      : activeTool.tool === "list_tables" ? "Listing tables"
      : activeTool.tool === "create_chart" ? "Creating chart"
      : activeTool.tool === "calculate" ? "Calculating"
      : activeTool.tool === "get_spec_limits" ? "Checking spec limits"
      : activeTool.tool === "get_product_info" ? "Looking up product"
      : activeTool.tool === "get_recent_failures" ? "Scanning for failures"
      : activeTool.tool === "get_production_sequence" ? "Tracing production"
      : activeTool.tool === "calculate_process_stats" ? "Computing process stats"
      : `Running ${activeTool.tool}`;

    if (runningTool) {
      statusText = `${toolLabel}${activeTool.input_summary ? ` — ${activeTool.input_summary}` : ""}`;
    } else {
      // All done so far, streaming text
      statusText = streaming.content.length > 0
        ? `Writing response... (${doneCount} tool${doneCount !== 1 ? "s" : ""} complete)`
        : `${toolLabel} complete${totalCount > 1 ? ` (${doneCount}/${totalCount})` : ""}`;
    }
  } else if (streaming.content.length > 0) {
    statusText = "Writing response...";
  }

  return (
    <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-200">
      <Loader2 className="h-3 w-3 animate-spin shrink-0 text-primary" />
      <span className="truncate">{statusText}</span>
    </div>
  );
}

// --- Relative time formatting ---

function timeAgo(dateStr: string): string {
  // SQLite datetime('now') returns UTC without Z suffix — append it so JS parses as UTC
  const utcStr = dateStr.endsWith("Z") || dateStr.includes("+") ? dateStr : dateStr + "Z";
  const diff = Date.now() - new Date(utcStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

// --- Conversation item ---

function ConversationItem({
  conv,
  activeId,
  onSelect,
  onDelete,
  onStar,
}: {
  conv: { id: string; title: string; message_count: number; is_starred: number; updated_at: string };
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: { mutate: (id: string) => void };
  onStar: { mutate: (data: { id: string; is_starred: boolean }) => void };
}) {
  return (
    <div
      className={`group flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-muted/50 transition-colors ${
        conv.id === activeId ? "bg-muted" : ""
      }`}
      onClick={() => onSelect(conv.id)}
    >
      <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-foreground">{conv.title}</p>
        <p className="text-xs text-muted-foreground">
          {conv.message_count} msg{conv.message_count !== 1 ? "s" : ""}
          {" \u00b7 "}
          {timeAgo(conv.updated_at)}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onStar.mutate({ id: conv.id, is_starred: !conv.is_starred });
        }}
        className={`mt-0.5 rounded p-0.5 transition-colors ${
          conv.is_starred
            ? "text-amber-500"
            : "text-muted-foreground/0 group-hover:text-muted-foreground hover:text-amber-500"
        }`}
        title={conv.is_starred ? "Unstar" : "Star"}
      >
        <Star className={`h-3.5 w-3.5 ${conv.is_starred ? "fill-current" : ""}`} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (confirm("Delete this conversation?")) {
            onDelete.mutate(conv.id);
          }
        }}
        className="mt-0.5 rounded p-0.5 text-muted-foreground/0 group-hover:text-muted-foreground hover:text-destructive transition-colors"
        title="Delete"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

// --- Conversation panel ---

function ConversationPanel({
  onSelect,
  onNew,
  activeId,
  onClose,
}: {
  onSelect: (id: string) => void;
  onNew: () => void;
  activeId: string | null;
  onClose: () => void;
}) {
  const { data: conversations, isLoading } = useConversations();
  const deleteConv = useDeleteConversation();
  const starConv = useStarConversation();

  const starred = conversations?.filter((c) => c.is_starred) ?? [];
  const recent = conversations?.filter((c) => !c.is_starred) ?? [];

  return (
    <div className="flex h-full flex-col border-r border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
          History
        </span>
        <div className="flex items-center gap-1">
          <button
            onClick={onNew}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="New conversation"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors sm:hidden"
            title="Close panel"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !conversations?.length ? (
          <p className="px-3 py-8 text-center text-xs text-muted-foreground">
            No conversations yet
          </p>
        ) : (
          <div className="py-1">
            {starred.length > 0 && (
              <>
                <p className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground/60 uppercase tracking-widest">Starred</p>
                {starred.map((conv) => (
                  <ConversationItem key={conv.id} conv={conv} activeId={activeId} onSelect={onSelect} onDelete={deleteConv} onStar={starConv} />
                ))}
                <p className="px-3 py-1.5 text-[10px] font-medium text-muted-foreground/60 uppercase tracking-widest">Recent</p>
              </>
            )}
            {recent.map((conv) => (
              <ConversationItem key={conv.id} conv={conv} activeId={activeId} onSelect={onSelect} onDelete={deleteConv} onStar={starConv} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Action bar (always visible below assistant messages) ---

const FEEDBACK_CATEGORIES = [
  "Wrong data",
  "Too slow",
  "Unclear",
  "Inaccurate",
  "Very helpful",
];

function ActionBar({ msg }: { msg: ChatMessage }) {
  const [copied, setCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [rating, setRating] = useState<number | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedCats, setSelectedCats] = useState<string[]>([]);
  const [feedbackText, setFeedbackText] = useState("");
  const submitFeedback = useSubmitFeedback();
  const createShareLink = useCreateShareLink();

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleThumbsUp = () => {
    if (!msg.conversationId) return;
    setRating(2);
    setShowDetail(false);
    submitFeedback.mutate({
      conversationId: msg.conversationId,
      messageId: msg.id,
      rating: 2,
    });
  };

  const handleThumbsDown = () => {
    setRating(0);
    setShowDetail(true);
  };

  const handleSubmitDetail = () => {
    if (!msg.conversationId) return;
    submitFeedback.mutate({
      conversationId: msg.conversationId,
      messageId: msg.id,
      rating: 0,
      text: feedbackText || undefined,
      categories: selectedCats.length ? selectedCats : undefined,
    });
    setShowDetail(false);
  };

  const toggleCat = (cat: string) => {
    setSelectedCats((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  return (
    <div className="mt-2 border-t border-border/50 pt-2">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        {/* Thumbs up */}
        <button
          onClick={handleThumbsUp}
          className={`rounded p-1 transition-colors ${
            rating === 2 ? "text-green-500" : "hover:text-green-500 hover:bg-muted/50"
          }`}
          title="Helpful"
        >
          <ThumbsUp className="h-3.5 w-3.5" />
        </button>
        {/* Thumbs down */}
        <button
          onClick={handleThumbsDown}
          className={`rounded p-1 transition-colors ${
            rating === 0 ? "text-orange-500" : "hover:text-orange-500 hover:bg-muted/50"
          }`}
          title="Not helpful"
        >
          <ThumbsDown className="h-3.5 w-3.5" />
        </button>

        {rating !== null && !showDetail && (
          <span className="ml-0.5 text-muted-foreground/50">Sent</span>
        )}

        <span className="mx-1.5 h-3 w-px bg-border" />

        {/* Copy */}
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-muted/50 hover:text-foreground transition-colors"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>

        {/* Share */}
        {msg.conversationId && (
          <>
            <span className="mx-1.5 h-3 w-px bg-border" />
            <button
              onClick={() => {
                if (!msg.conversationId) return;
                createShareLink.mutate(
                  { conversation_id: msg.conversationId },
                  {
                    onSuccess: (data) => {
                      const url = `${window.location.origin}/shared/${data.share_token}`;
                      navigator.clipboard.writeText(url);
                      setShareCopied(true);
                      setTimeout(() => setShareCopied(false), 2000);
                    },
                  }
                );
              }}
              disabled={createShareLink.isPending}
              className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-muted/50 hover:text-foreground transition-colors"
            >
              {shareCopied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Link className="h-3.5 w-3.5" />}
              <span>{shareCopied ? "Link copied" : "Share"}</span>
            </button>
          </>
        )}

      </div>

      {/* Feedback detail panel (expanded on thumbs down) */}
      {showDetail && (
        <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3 text-xs">
          <div className="mb-2 flex flex-wrap gap-1.5">
            {FEEDBACK_CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => toggleCat(cat)}
                className={`rounded-full border px-2.5 py-1 transition-colors ${
                  selectedCats.includes(cat)
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/50"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            placeholder="What went wrong? (optional)"
            rows={2}
            className="mb-2 w-full resize-none rounded border border-border bg-background px-2 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <div className="flex gap-2">
            <button
              onClick={handleSubmitDetail}
              className="rounded bg-primary px-3 py-1 text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Submit
            </button>
            <button
              onClick={() => {
                setShowDetail(false);
                setRating(null);
              }}
              className="rounded px-3 py-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// --- Model selector ---

function ModelSelector({
  model,
  onModelChange,
}: {
  model: string;
  onModelChange: (m: string) => void;
}) {
  const { models, fetchModels } = useChatModels();

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  if (!models.length) return null;

  return (
    <select
      value={model}
      onChange={(e) => onModelChange(e.target.value)}
      className="rounded border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
    >
      {models.map((m) => (
        <option key={m.id} value={m.id} disabled={m.available === false}>
          {m.name}
        </option>
      ))}
    </select>
  );
}

// --- Memory panel ---

function MemoryPanel({ onClose }: { onClose: () => void }) {
  const { data: memories, isLoading } = useMemories();
  const createMemory = useCreateMemory();
  const deleteMemory = useDeleteMemory();
  const [newMemory, setNewMemory] = useState("");

  const handleAdd = () => {
    if (!newMemory.trim()) return;
    createMemory.mutate(newMemory.trim());
    setNewMemory("");
  };

  return (
    <div className="flex h-full flex-col border-r border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-primary" />
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Memory
          </span>
        </div>
        <button
          onClick={onClose}
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-3">
        <p className="mb-2 text-xs text-muted-foreground">
          Memories personalise your AI experience. They're automatically extracted from conversations.
        </p>
        <div className="flex gap-1.5">
          <input
            type="text"
            value={newMemory}
            onChange={(e) => setNewMemory(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            placeholder="Add a memory..."
            className="flex-1 rounded border border-border bg-background px-2 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <button
            onClick={handleAdd}
            disabled={!newMemory.trim()}
            className="rounded bg-primary px-2 py-1.5 text-xs text-primary-foreground disabled:opacity-50 hover:bg-primary/90 transition-colors"
          >
            Add
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !memories?.length ? (
          <div className="py-6 text-center text-xs text-muted-foreground space-y-2">
            <p>No memories yet.</p>
            <p>Tell me about your role and which products you focus on, and I'll remember for next time.</p>
            <p className="text-muted-foreground/50">Memories are automatically extracted from conversations.</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {memories.map((mem) => (
              <div
                key={mem.id}
                className="group flex items-start gap-2 rounded-md border border-border/50 bg-muted/30 px-2.5 py-2 text-xs"
              >
                {mem.source === "auto" ? (
                  <span title="Auto-extracted"><Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-amber-500/70" /></span>
                ) : (
                  <span title="Manually added"><Brain className="mt-0.5 h-3 w-3 shrink-0 text-primary/70" /></span>
                )}
                <span className="flex-1 text-foreground leading-relaxed">{mem.content}</span>
                <button
                  onClick={() => deleteMemory.mutate(mem.id)}
                  className="mt-0.5 rounded p-0.5 text-muted-foreground/0 group-hover:text-muted-foreground hover:text-destructive transition-colors"
                  title="Delete memory"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// --- Main page ---

export function DataAnalyst() {
  const {
    messages,
    isLoading,
    hasAttachments,
    error,
    model,
    setModel,
    sendMessage,
    clearChat,
    conversationId,
    loadConversation,
    streamingMessage,
  } = useChat();

  const [showHistory, setShowHistory] = useState(true);
  const [showMemory, setShowMemory] = useState(false);
  const { data: memories } = useMemories();
  const hasMemories = (memories?.length ?? 0) > 0;
  const { data: discoverPrompts } = useDiscoverPrompts();
  const displayPrompts = discoverPrompts?.length ? discoverPrompts : FALLBACK_PROMPTS;

  const [input, setInput] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading, streamingMessage]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // File size limits per type
  const FILE_LIMITS: Record<string, number> = {
    image: 20 * 1024 * 1024,     // 20MB (will be resized if needed)
    pdf: 30 * 1024 * 1024,       // 30MB
    office: 10 * 1024 * 1024,    // 10MB
    text: 1 * 1024 * 1024,       // 1MB
  };

  // Resize images >1568px or >1MB using Canvas (Claude's optimal max dimension)
  const MAX_IMAGE_DIM = 1568;
  const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1MB — resize if larger
  const resizeImage = useCallback((file: File): Promise<{ base64: string; mediaType: string; sizeBytes: number }> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const { naturalWidth: w, naturalHeight: h } = img;
        const needsResize = file.size > MAX_IMAGE_BYTES || w > MAX_IMAGE_DIM || h > MAX_IMAGE_DIM;

        if (!needsResize) {
          // Small enough — encode original
          const reader = new FileReader();
          reader.onload = () => {
            const base64 = (reader.result as string).split(",")[1];
            resolve({ base64, mediaType: file.type, sizeBytes: file.size });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
          return;
        }

        // Calculate scaled dimensions (fit within MAX_IMAGE_DIM box)
        const scale = Math.min(MAX_IMAGE_DIM / w, MAX_IMAGE_DIM / h, 1);
        const newW = Math.round(w * scale);
        const newH = Math.round(h * scale);

        const canvas = document.createElement("canvas");
        canvas.width = newW;
        canvas.height = newH;
        const ctx = canvas.getContext("2d");
        if (!ctx) { reject(new Error("Canvas not supported")); return; }
        ctx.drawImage(img, 0, 0, newW, newH);

        // Export as JPEG 85% quality (much smaller than PNG for photos)
        canvas.toBlob(
          (blob) => {
            if (!blob) { reject(new Error("Failed to resize")); return; }
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(",")[1];
              resolve({ base64, mediaType: "image/jpeg", sizeBytes: blob.size });
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          "image/jpeg",
          0.85
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Failed to load image")); };
      img.src = url;
    });
  }, []);

  const getFileCategory = (mimeType: string): string => {
    if (mimeType.startsWith("image/")) return "image";
    if (mimeType === "application/pdf") return "pdf";
    if (mimeType.includes("officedocument") || mimeType.includes("opendocument")) return "office";
    return "text";
  };

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    const newAttachments: Attachment[] = [];

    for (const file of Array.from(files)) {
      const category = getFileCategory(file.type || "text/plain");
      const limit = FILE_LIMITS[category] || FILE_LIMITS.text;

      if (file.size > limit) {
        setFileError(`${file.name} is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max: ${(limit / 1024 / 1024).toFixed(0)}MB`);
        continue;
      }

      // Determine attachment type
      let type: Attachment["type"];
      if (file.type.startsWith("image/")) {
        type = "image";
      } else if (file.type === "application/pdf") {
        type = "document";
      } else if (file.type.includes("officedocument") || file.type.includes("opendocument")) {
        type = "document";
      } else {
        type = "text";
      }

      // Read file
      if (type === "text") {
        const text = await file.text();
        newAttachments.push({
          type: "text",
          mediaType: file.type || "text/plain",
          data: text,
          filename: file.name,
          sizeBytes: file.size,
        });
      } else if (type === "image") {
        // Images: resize if needed (>1568px or >1MB) then base64 encode
        try {
          const { base64, mediaType, sizeBytes } = await resizeImage(file);
          newAttachments.push({
            type: "image",
            mediaType,
            data: base64,
            filename: file.name,
            sizeBytes,
          });
        } catch {
          setFileError(`Failed to process ${file.name}`);
        }
      } else {
        // base64 encode for PDFs, office docs
        const buffer = await file.arrayBuffer();
        const binary = new Uint8Array(buffer);
        let base64 = "";
        const CHUNK = 8192;
        for (let i = 0; i < binary.length; i += CHUNK) {
          base64 += String.fromCharCode(...binary.subarray(i, i + CHUNK));
        }
        base64 = btoa(base64);

        newAttachments.push({
          type,
          mediaType: file.type,
          data: base64,
          filename: file.name,
          sizeBytes: file.size,
        });
      }
    }

    setPendingAttachments((prev) => [...prev, ...newAttachments]);
    // Reset input so re-selecting the same file works
    e.target.value = "";
  }, []);

  const removeAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSend = useCallback(() => {
    if ((!input.trim() && !pendingAttachments.length) || isLoading) return;
    setFileError(null);
    sendMessage(input, pendingAttachments.length ? pendingAttachments : undefined);
    setInput("");
    setPendingAttachments([]);
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
  }, [input, isLoading, sendMessage, pendingAttachments]);

  const saveArtifact = useSaveArtifact();
  const handlePinChart = useCallback((spec: ChartSpec, sql?: string) => {
    saveArtifact.mutate({
      title: spec.title || "Untitled chart",
      type: "chart",
      spec_json: spec,
      sql_query: sql,
    });
  }, [saveArtifact]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  };

  useSetPageHeader({
    title: "Data Analyst",
    icon: BrainCircuit,
    actions: (
      <>
        <button
          onClick={() => { setShowHistory(!showHistory); if (!showHistory) setShowMemory(false); }}
          className={`rounded-md p-1.5 transition-colors ${
            showHistory
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          title="Chat history"
        >
          <MessageSquare className="h-4 w-4" />
        </button>
        <button
          onClick={() => { setShowMemory(!showMemory); if (!showMemory) setShowHistory(false); }}
          className={`rounded-md p-1.5 transition-colors ${
            showMemory
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted hover:text-foreground"
          }`}
          title="Memory"
        >
          <Brain className="h-4 w-4" />
        </button>
        <ModelSelector model={model} onModelChange={setModel} />
        {messages.length > 0 && (
          <button
            onClick={clearChat}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title="New conversation"
          >
            <Plus className="h-4 w-4" />
          </button>
        )}
      </>
    ),
  });

  return (
    <div className="flex h-full flex-col">
      {/* Body — history panel + messages */}
      <div className="flex flex-1 overflow-hidden">
        {/* Conversation panel */}
        {showHistory && (
          <div className="w-64 shrink-0 overflow-hidden max-sm:absolute max-sm:inset-y-0 max-sm:left-0 max-sm:z-20 max-sm:w-72 max-sm:shadow-lg">
            <ConversationPanel
              onSelect={(id) => {
                loadConversation(id);
                setShowHistory(false);
              }}
              onNew={() => {
                clearChat();
                setShowHistory(false);
              }}
              activeId={conversationId}
              onClose={() => setShowHistory(false)}
            />
          </div>
        )}

        {/* Memory panel */}
        {showMemory && (
          <div className="w-64 shrink-0 overflow-hidden max-sm:absolute max-sm:inset-y-0 max-sm:left-0 max-sm:z-20 max-sm:w-72 max-sm:shadow-lg">
            <MemoryPanel onClose={() => setShowMemory(false)} />
          </div>
        )}

        {/* Messages column */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
            {messages.length === 0 ? (
              <div className="flex h-full flex-col items-center justify-center">
                <BrainCircuit className="mb-4 h-12 w-12 text-muted-foreground/30" />
                <h2 className="mb-2 text-lg font-medium text-foreground">
                  Ask me anything about PlastiQ data
                </h2>
                <p className="mb-6 max-w-md text-center text-sm text-muted-foreground">
                  I can query your production, quality, lab, and R&D data.
                  I'll show you the SQL I run so you can verify the results.
                </p>
                {/* Onboarding card when no memories exist */}
                {!hasMemories && (
                  <button
                    onClick={() => sendMessage("Hi! I'd like to learn about PlastiQ's production data.")}
                    className="mb-4 flex w-full max-w-xl items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-left text-sm transition-colors hover:bg-primary/10"
                  >
                    <UserCircle className="h-5 w-5 shrink-0 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">Introduce yourself</p>
                      <p className="text-xs text-muted-foreground">Tell me your role and which products you focus on, and I'll personalise your experience.</p>
                    </div>
                  </button>
                )}
                <div className="flex w-full max-w-xl flex-col gap-2">
                  {displayPrompts.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => sendMessage(p.prompt_text)}
                      className="rounded-lg border border-border bg-card px-4 py-3 text-left text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      <span className="mr-2">{CATEGORY_ICONS[p.category] || CATEGORY_ICONS.general}</span>
                      {p.prompt_text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mx-auto max-w-3xl space-y-4">
                {messages.map((msg, i) => {
                  const isLastAssistant = !isLoading && msg.role === "assistant" && i === messages.length - 1;
                  return (
                    <MessageBubble
                      key={msg.id}
                      msg={msg}
                      onSendFollowUp={isLastAssistant ? sendMessage : undefined}
                      onPinChart={handlePinChart}
                    />
                  );
                })}

                {isLoading && (streamingMessage
                  ? <StreamingBubble streaming={streamingMessage} />
                  : <AnalysingIndicator hasAttachments={hasAttachments} />
                )}

                {(error || fileError) && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4 shrink-0" />
                      {error || fileError}
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Input area */}
          <div className="px-4 py-3 sm:px-6">
            <div className="mx-auto max-w-3xl">
              {/* Streaming status line */}
              {isLoading && streamingMessage && (
                <StreamingStatus streaming={streamingMessage} />
              )}

              {/* Attachment previews */}
              {pendingAttachments.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {pendingAttachments.map((att, i) => (
                    <div key={i} className="flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-1 text-xs">
                      {att.type === "image" ? (
                        <ImageIcon className="h-3 w-3 shrink-0 text-blue-500" />
                      ) : att.mediaType === "application/pdf" ? (
                        <FileText className="h-3 w-3 shrink-0 text-red-500" />
                      ) : (
                        <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                      )}
                      <span className="max-w-[120px] truncate text-foreground">{att.filename}</span>
                      <span className="text-muted-foreground">
                        {att.sizeBytes < 1024 ? `${att.sizeBytes}B` : att.sizeBytes < 1048576 ? `${(att.sizeBytes / 1024).toFixed(0)}KB` : `${(att.sizeBytes / 1048576).toFixed(1)}MB`}
                      </span>
                      <button
                        onClick={() => removeAttachment(i)}
                        className="ml-0.5 rounded p-0.5 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-end gap-2">
                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/jpeg,image/png,image/gif,image/webp,.pdf,.docx,.xlsx,.ods,.odt,.csv,.txt,.json"
                  className="hidden"
                  onChange={handleFileSelect}
                />

                {/* Paperclip button */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground disabled:opacity-50 hover:bg-muted hover:text-foreground transition-colors"
                  title="Attach files"
                >
                  <Paperclip className="h-4 w-4" />
                </button>

                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about production, quality, lab data..."
                  rows={1}
                  className="flex-1 resize-none rounded-lg border border-border bg-background px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  disabled={isLoading}
                />
                <button
                  onClick={handleSend}
                  disabled={(!input.trim() && !pendingAttachments.length) || isLoading}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-50 hover:bg-primary/90 transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>{/* end messages column */}
      </div>{/* end flex body */}
    </div>
  );
}
